<template>
  <div
    v-if="shouldShow"
    class="fair-survey-step"
  >
    <div class="fair-survey-step__head">
      <h2 class="fair-survey-step__title">
        {{ step.title }}
      </h2>
      <p
        v-if="step.subtitle"
        class="fair-survey-step__subtitle"
      >
        {{ step.subtitle }}
      </p>
    </div>

    <!-- Shoe sizes are quoted in three systems; the reel follows the pick. -->
    <div
      v-if="isShoeSizeStep"
      class="measurement-step__systems"
      role="group"
      aria-label="Система размеров"
    >
      <button
        v-for="system in shoeSizeSystems"
        :key="system"
        type="button"
        class="measurement-step__system"
        :class="{
          'measurement-step__system--active': selectedShoeSizeSystem === system,
        }"
        :aria-pressed="selectedShoeSizeSystem === system"
        @click="selectShoeSizeSystem(system)"
      >
        {{ system }}
      </button>
    </div>

    <FairDrumPicker
      v-model="selectedValue"
      :items="pickerValues"
      :orientation="effectiveOrientation"
      :unit="isShoeSizeStep ? '' : effectiveUnit"
      :aria-label="step.title"
    />

    <button
      v-if="step.hint"
      type="button"
      class="measurement-step__hint-toggle"
      @click="showHint = !showHint"
    >
      <span>{{ step.hint.title }}</span>
      <FairIcon
        :name="showHint ? 'caret-up' : 'caret-down'"
        :size="16"
      />
    </button>

    <div
      v-if="showHint && step.hint"
      class="fair-survey-step__hint"
    >
      <video
        v-if="step.hint.video_src"
        class="fair-survey-step__hint-media"
        :src="step.hint.video_src"
        autoplay
        loop
        muted
        playsinline
      />
      <img
        v-else
        class="fair-survey-step__hint-media"
        :src="measureImage"
        alt=""
      >
      <p class="fair-survey-step__hint-text">
        {{ step.hint.description }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { FairDrumPicker } from '@/ui/primitives/FairDrumPicker'
import { FairIcon } from '@/ui/primitives/FairIcon'
import { useSurveyModal } from '@/features/survey/store'
import type { SurveyStep } from '@/features/survey/types/survey-config'
import {
  type ShoeSizeSystem,
  convertShoeSize,
  generateShoeSizes,
  getShoeSizeRange
} from '@/utils/shoeSizeConverter'

interface Props {
  step: SurveyStep & ({ type: 'measurement' } | { type: 'shoe_size' })
}

const props = defineProps<Props>()
const surveyStore = useSurveyModal()
const selectedValue = ref<number | null>(null)
const showHint = ref(false)

// Shoe size system state
const isShoeSizeStep = computed(() => props.step.type === 'shoe_size')
const shoeSizeSystems: ShoeSizeSystem[] = ['EUR', 'UK', 'US']
const selectedShoeSizeSystem = ref<ShoeSizeSystem>('EUR')

const measureImage = 'img/survey/height_img/height.png'

// Используем shouldStepBeVisible из store для правильной обработки условной логики
// (поддержка как старого формата field/value, так и нового формата с rules)
const shouldShow = computed(() => {
  return surveyStore.shouldStepBeVisible(props.step)
})

// Дефолтные значения для shoe_size
const defaultShoeSizeUnit = 'размер'
const defaultShoeSizeOrientation = 'horizontal' as const

// Computed для получения эффективных значений с дефолтами для shoe_size
const effectiveRange = computed(() => {
  if (props.step.type === 'shoe_size') {
    // Для shoeSize используем диапазон из выбранной системы
    return getShoeSizeRange(selectedShoeSizeSystem.value)
  }
  return props.step.range
})

const effectiveOrientation = computed(() => {
  if (props.step.type === 'shoe_size') {
    return props.step.orientation || defaultShoeSizeOrientation
  }
  return props.step.orientation
})

const effectiveUnit = computed(() => {
  if (props.step.type === 'shoe_size') {
    return props.step.unit || defaultShoeSizeUnit
  }
  return props.step.unit
})

// Мемоизируем pickerOptions, чтобы не пересчитывать при каждом рендере
const pickerOptions = computed(() => {
  // Для shoeSize используем размеры из выбранной системы
  if (isShoeSizeStep.value) {
    return generateShoeSizes(selectedShoeSizeSystem.value)
  }

  // Для обычных измерений используем стандартную логику
  const { min, max, step: stepValue = 1 } = effectiveRange.value
  const count = Math.floor((max - min) / stepValue) + 1
  // Используем предварительно созданный массив для лучшей производительности
  const options = new Array(count)
  for (let i = 0; i < count; i++) {
    const val = min + i * stepValue
    options[i] = {
      value: val,
      label: String(val)
    }
  }
  return options
})

/** The drum takes plain numbers; labels are its own concern. */
const pickerValues = computed(() =>
  pickerOptions.value.map((option: { value: number }) => option.value),
)

let updateTimeout: ReturnType<typeof setTimeout> | null = null

function updateValue(value: number | null) {
  // Debounce обновление данных, чтобы избежать лишних пересчетов при прокрутке picker
  if (updateTimeout) {
    clearTimeout(updateTimeout)
  }
  updateTimeout = setTimeout(() => {
    // Используем step.id как ключ, так как бэкенд возвращает measurement_type (snake_case)
    // и оно может быть undefined. step.id всегда есть и соответствует ключу в answers
    surveyStore.updateSurveyData(props.step.id, value)
    
    // Для shoeSize также сохраняем выбранную систему
    if (isShoeSizeStep.value) {
      surveyStore.updateSurveyData(`${props.step.id}_system`, selectedShoeSizeSystem.value)
    }
    
    updateTimeout = null
  }, 150) // Обновляем через 150ms после последнего изменения
}

function selectShoeSizeSystem(system: ShoeSizeSystem) {
  if (selectedShoeSizeSystem.value === system) return

  const oldSystem = selectedShoeSizeSystem.value
  selectedShoeSizeSystem.value = system

  // Конвертируем текущее значение в новую систему
  if (selectedValue.value !== null) {
    const convertedValue = convertShoeSize(selectedValue.value, oldSystem, system)
    if (convertedValue !== null) {
      // Находим ближайшее доступное значение в новой системе
      const availableSizes = generateShoeSizes(system)
      const closestSize = availableSizes.reduce((prev, curr) => {
        const prevDiff = Math.abs(prev.value - convertedValue)
        const currDiff = Math.abs(curr.value - convertedValue)
        return currDiff < prevDiff ? curr : prev
      })
      selectedValue.value = closestSize.value
    } else {
      // Если конвертация не удалась, устанавливаем первое доступное значение
      const firstSize = generateShoeSizes(system)[0]
      if (firstSize) {
        selectedValue.value = firstSize.value
      }
    }
  }

  // Сохраняем выбранную систему в store
  surveyStore.updateSurveyData(`${props.step.id}_system`, system)
}

watch(selectedValue, updateValue)

function initValueFromStep() {
  if (isShoeSizeStep.value) {
    const savedSystem = surveyStore.surveyModal.surveyData[`${props.step.id}_system`] as ShoeSizeSystem | undefined
    if (savedSystem && shoeSizeSystems.includes(savedSystem)) {
      selectedShoeSizeSystem.value = savedSystem
    }

    const savedValue = surveyStore.surveyModal.surveyData[props.step.id]
    if (savedValue !== null && savedValue !== undefined) {
      selectedValue.value = savedValue as number
    } else {
      const firstSize = generateShoeSizes(selectedShoeSizeSystem.value)[0]
      if (firstSize) {
        selectedValue.value = firstSize.value
      }
    }
  } else {
    const saved = surveyStore.surveyModal.surveyData[props.step.id]
    // No answer yet: start in the middle of the range. The floor of a height
    // scale is a value nobody has, so it reads as a broken default and costs
    // the user a long scroll before the first plausible number.
    selectedValue.value =
      saved ?? pickerValues.value[Math.floor(pickerValues.value.length / 2)] ?? null
  }
}

onMounted(initValueFromStep)

// При смене шага (тот же компонент переиспользуется для другого step) сбрасываем значение на данные текущего шага
watch(
  () => props.step.id,
  () => {
    initValueFromStep()
  }
)
</script>

<style scoped>
.measurement-step__systems {
  display: flex;
  align-self: center;
  align-items: center;
  gap: var(--space-8);
}

.measurement-step__system {
  display: inline-flex;
  align-items: center;
  margin: 0;
  padding: var(--space-8) var(--space-16);
  border: 0;
  border-radius: var(--radius-full);
  background: var(--color-fill-chip);
  font: var(--type-body-ui);
  letter-spacing: var(--type-body-ui-tracking);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.measurement-step__system:hover {
  color: var(--color-text-primary);
}

.measurement-step__system--active {
  background: var(--color-primary);
  color: var(--color-text-on-dark);
}

.measurement-step__hint-toggle {
  display: inline-flex;
  align-self: center;
  align-items: center;
  gap: var(--space-8);
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  font: var(--type-body-ui);
  letter-spacing: var(--type-body-ui-tracking);
  color: var(--color-text-muted);
  cursor: pointer;
  transition: color 0.15s ease;
}

.measurement-step__hint-toggle:hover {
  color: var(--color-text-primary);
}

@media (prefers-reduced-motion: reduce) {
  .measurement-step__system,
  .measurement-step__hint-toggle {
    transition: none;
  }
}
</style>
